import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip, ReferenceArea, ReferenceLine,
} from 'recharts';

const MCID = 13; // Diferencia mínima clínicamente importante (Todd et al. 1996)

// Categoría de dolor según valor VAS (0-100 mm)
const categoria = (v) => {
  if (v == null) return { label: '—', color: '#64748b' };
  if (v <= 4) return { label: 'Sin dolor', color: '#16a34a' };
  if (v <= 30) return { label: 'Dolor leve', color: '#16a34a' };
  if (v <= 69) return { label: 'Dolor moderado', color: '#ea580c' };
  return { label: 'Dolor severo', color: '#dc2626' };
};

const fmtFecha = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return s; }
};

/**
 * Visualización clínica del VAS/EVA de dolor:
 * - valor actual + categoría + cambio vs inicial
 * - gráfica con zonas (leve/moderado/severo) + línea MCID
 * - narrativa de evolución con significación clínica (MCID) + balance global
 *
 * Props: mediciones = [{ fecha, value }] (cualquier orden; se ordenan asc.)
 */
const VasEvolucionClinica = ({ mediciones = [] }) => {
  const serie = [...mediciones]
    .filter((m) => m.value != null && !isNaN(Number(m.value)))
    .map((m) => ({ fecha: m.fecha, value: Number(m.value) }))
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

  if (serie.length === 0) {
    return <p className="text-xs text-gray-400">Sin mediciones de dolor registradas.</p>;
  }

  const primero = serie[0].value;
  const ultimo = serie[serie.length - 1].value;
  const catActual = categoria(ultimo);
  const deltaTotal = ultimo - primero;
  const pctMejoria = primero > 0 ? Math.round((-deltaTotal / primero) * 100) : 0;

  const chartData = serie.map((p, i) => ({ ...p, idx: i, label: fmtFecha(p.fecha) }));

  // Narrativa por medición
  const narrativa = serie.map((p, i) => {
    if (i === 0) {
      const c = categoria(p.value);
      return { tipo: 'inicio', fecha: p.fecha, value: p.value, cat: c.label };
    }
    const delta = p.value - serie[i - 1].value;
    const sig = Math.abs(delta) >= MCID;
    const mejora = delta < 0;
    return {
      tipo: 'cambio', fecha: p.fecha, value: p.value, delta, sig, mejora,
    };
  });

  return (
    <div className="bg-white rounded-lg border p-4">
      {/* Cabecera · valor actual */}
      <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-4xl font-extrabold" style={{ color: catActual.color }}>{ultimo}</span>
          <span className="text-sm text-gray-400 -ml-1">mm</span>
          <span className="text-base font-bold" style={{ color: catActual.color }}>{catActual.label}</span>
          {serie.length > 1 && (
            <span className="text-sm font-bold" style={{ color: deltaTotal <= 0 ? '#16a34a' : '#dc2626' }}>
              ({deltaTotal <= 0 ? '' : '+'}{deltaTotal}mm {deltaTotal <= 0 ? 'mejoría' : 'empeoramiento'})
            </span>
          )}
        </div>
        <span className="text-[11px] text-gray-400">VAS 0-100mm · MCID: {MCID}mm</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Gráfica con zonas */}
        <div className="lg:col-span-2" style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 16, right: 16, bottom: 8, left: 0 }}>
              {/* Zonas de severidad */}
              <ReferenceArea y1={0} y2={30} fill="#16a34a" fillOpacity={0.07} />
              <ReferenceArea y1={30} y2={70} fill="#ea580c" fillOpacity={0.06} />
              <ReferenceArea y1={70} y2={100} fill="#dc2626" fillOpacity={0.07} />
              {/* Etiquetas de zona */}
              <ReferenceLine y={70} stroke="#fecaca" strokeDasharray="0" label={{ value: 'Severo', position: 'insideTopRight', fontSize: 10, fill: '#dc2626' }} />
              <ReferenceLine y={30} stroke="#bbf7d0" label={{ value: 'Moderado', position: 'insideTopRight', fontSize: 10, fill: '#ea580c' }} />
              {/* MCID */}
              <ReferenceLine y={MCID} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: 'MCID', position: 'right', fontSize: 9, fill: '#94a3b8' }} />

              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} ticks={[0, 30, 70, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={28} />
              <Tooltip
                contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                formatter={(v) => [`${v} mm — ${categoria(v).label}`, 'VAS']}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#1e293b"
                strokeWidth={2}
                isAnimationActive
                animationDuration={900}
                dot={(props) => {
                  const c = categoria(props.payload.value).color;
                  return <circle key={props.cx} cx={props.cx} cy={props.cy} r={5} fill="#fff" stroke={c} strokeWidth={2.5} />;
                }}
                label={{ position: 'top', fontSize: 11, fontWeight: 700, fill: '#334155', offset: 10 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Narrativa clínica */}
        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Evolución clínica</p>
          {narrativa.map((n, i) => (
            <p key={i} className="text-xs text-gray-700 leading-snug">
              {n.tipo === 'inicio' ? (
                <>El paciente inició con un VAS de <strong>{n.value}mm</strong> ({n.cat.toLowerCase()}) el {fmtFecha(n.fecha)}.</>
              ) : (
                <>
                  El {fmtFecha(n.fecha)}: <strong>{n.value}mm</strong> ({n.delta <= 0 ? '' : '+'}{n.delta}mm).{' '}
                  {n.sig ? (
                    <span style={{ color: n.mejora ? '#16a34a' : '#dc2626' }}>
                      {n.mejora ? 'Mejoría clínicamente significativa.' : 'Empeoramiento clínicamente significativo.'}
                    </span>
                  ) : (
                    <span className="text-gray-400">Cambio no significativo (&lt; MCID).</span>
                  )}
                </>
              )}
            </p>
          ))}
          {serie.length > 1 && (
            <p className="text-xs text-gray-700 pt-2 border-t">
              <strong>Balance global:</strong>{' '}
              {deltaTotal <= 0
                ? <>mejoría de {Math.abs(deltaTotal)}mm ({pctMejoria}%) en {serie.length} mediciones.</>
                : <>empeoramiento de {deltaTotal}mm en {serie.length} mediciones.</>}
            </p>
          )}
        </div>
      </div>

      <p className="text-[10px] text-gray-400 text-center mt-3">
        Escala Visual Analógica (VAS) — Cortes: Collins et al. 1997, Boonstra et al. 2014 — MCID: Todd et al. 1996
      </p>
    </div>
  );
};

export default VasEvolucionClinica;
